"use client";

import { useState, useEffect, useMemo } from "react";
import Step2AddressInput from "./Step2AddressInput";
import Step2ElevatorSelect from "./Step2ElevatorSelect";
import Step2LocationTypePicker from "./Step2LocationTypePicker";
import AddressDetailInput from "./AddressDetailInput";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useOrderStore } from "@/store/useOrderStore";
import { checkServiceArea, getServiceAreas } from "@/lib/service-area";
import { orderRequiresCarryHelp } from "@/lib/carry-help";
import { orderRequiresInsidePickup, orderRequiresInsideDelivery } from "@/lib/disassembly";
import { calcLocationAdjustments } from "@/lib/price-calculator";
import { useAdminMode } from "@/hooks/useAdminMode";
import { cn } from "@/lib/utils";
import type { AddressInfo, LocationType } from "@/types/address";
import type { AddressInfo as AddressInfoStore } from "@/types/order";

interface Props {
  onComplete: () => void;
  skipServiceAreaCheck?: boolean;
  /** 견적 문의 흐름: 물품 정보가 없어 위치별 금액을 계산할 수 없으므로 라벨만 표시 */
  quoteMode?: boolean;
  /** 서비스 지역 밖일 때 견적 문의 모드로 전환 */
  onSwitchToQuote?: () => void;
}

interface AreaError {
  city: string;
  district: string;
}

const SIDE_GUIDANCE: Record<"origin" | "destination", Record<LocationType, string>> = {
  origin: {
    INSIDE: "세부주소 안에서 물품을 수거합니다.",
    DOOR: "세부주소 문앞에서 물품을 수거합니다.",
    PARKING:
      "물품의 수거 위치와 주차 위치 및 방법을 알려주세요. 주차위치와 물품수거위치 사이에 계단이 없어야해요. 물품 도난 및 분실의 우려가 없는 곳이거나 물품을 건네주실 분이 계셔야 합니다.",
  },
  destination: {
    INSIDE: "세부주소 안까지 물품을 배송합니다.",
    DOOR: "세부주소 문앞까지 물품을 배송합니다.",
    PARKING:
      "물품의 배송 위치와 주차 위치 및 방법을 알려주세요. 주차위치와 물품배송위치 사이에 계단이 없어야해요. 물품 도난 및 분실의 우려가 없는 곳이거나 물품을 건네받으신 분이 계셔야 합니다.",
  },
};

const INSIDE_PICKUP_MESSAGE = "분해를 위해서는 집안 수거만 가능해요.";
const INSIDE_DELIVERY_MESSAGE = "조립을 위해서는 집안 배송만 가능해요.";

export default function Step2Address({ onComplete, skipServiceAreaCheck = false, quoteMode = false, onSwitchToQuote }: Props) {
  const { origin, destination, setOrigin, setDestination, items, itemInfo } = useOrderStore();
  const { isAdmin } = useAdminMode();
  const carryHelpRequired = orderRequiresCarryHelp(items, itemInfo);
  const insidePickupRequired = orderRequiresInsidePickup(items, itemInfo);
  const insideDeliveryRequired = orderRequiresInsideDelivery(items, itemInfo);
  const [attempted, setAttempted] = useState(false);
  const [originAreaError, setOriginAreaError] = useState<AreaError | null>(null);
  const [destAreaError, setDestAreaError] = useState<AreaError | null>(null);
  const [areaText, setAreaText] = useState("");

  useEffect(() => {
    if (skipServiceAreaCheck) return;
    getServiceAreas().then((areas) => {
      if (areas.length === 0) return;
      const districts = areas.map((a) => a.district.replace("구", "")).join(", ");
      setAreaText(
        `현재는 서울시 ${areas.length}개 구(${districts})에서만 서비스를 제공하고 있습니다. 빠르게 서비스 지역을 확장할 수 있도록 하겠습니다.`,
      );
    });
  }, [skipServiceAreaCheck]);

  // 분해/조립이 추가된 직후, 이미 비-INSIDE 가 선택돼 있다면 자동 초기화 (해당 side 만)
  useEffect(() => {
    if (insidePickupRequired && origin?.locationType && origin.locationType !== "INSIDE") {
      setOrigin({ ...origin, locationType: null });
    }
    if (insideDeliveryRequired && destination?.locationType && destination.locationType !== "INSIDE") {
      setDestination({ ...destination, locationType: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insidePickupRequired, insideDeliveryRequired]);

  // 기존 AddressInfo → AddressInfo (input 컴포넌트가 기대하는 모양)
  const convertToAddressInfo = (info: typeof origin): AddressInfo | null => {
    if (!info) return null;
    return {
      address: info.address,
      addressDetail: info.addressDetail,
      zonecode: (info as AddressInfo).zonecode || "",
      roadAddress: (info as AddressInfo).roadAddress || info.address,
      jibunAddress: (info as AddressInfo).jibunAddress || "",
      buildingName: (info as AddressInfo).buildingName || "",
      lat: info.lat,
      lng: info.lng,
      hasElevator: info.hasElevator,
      floor: info.floor,
      carryHelp: info.carryHelp,
      locationType: info.locationType,
      parkingInfo: info.parkingInfo,
    };
  };

  const buildNextSide = (
    info: AddressInfo,
    prev: AddressInfoStore | null,
  ): AddressInfoStore => {
    const base: AddressInfoStore = {
      address: info.address,
      addressDetail: info.addressDetail,
      lat: info.lat || 0,
      lng: info.lng || 0,
      hasElevator: info.hasElevator,
      floor: info.floor,
      carryHelp: prev?.carryHelp,
      locationType: prev?.locationType ?? null,
      parkingInfo: prev?.parkingInfo,
    };
    // 상세주소 첫 글자가 1~5 + 엘리베이터 없음이면 층수 자동 선택
    if (!info.hasElevator && info.addressDetail) {
      const firstChar = info.addressDetail.charAt(0);
      if (firstChar >= "1" && firstChar <= "5") {
        base.floor = Number(firstChar);
      }
    }
    return base;
  };

  const handleOriginChange = async (info: AddressInfo) => {
    if (skipServiceAreaCheck) {
      setOriginAreaError(null);
    } else {
      const result = await checkServiceArea(info.address);
      if (!result.available) {
        setOriginAreaError({ city: result.city, district: result.district });
      } else {
        setOriginAreaError(null);
      }
    }
    setOrigin(buildNextSide(info, origin));
  };

  const handleDestinationChange = async (info: AddressInfo) => {
    if (skipServiceAreaCheck) {
      setDestAreaError(null);
    } else {
      const result = await checkServiceArea(info.address);
      if (!result.available) {
        setDestAreaError({ city: result.city, district: result.district });
      } else {
        setDestAreaError(null);
      }
    }
    setDestination(buildNextSide(info, destination));
  };

  const originInfo = convertToAddressInfo(origin);
  const destInfo = convertToAddressInfo(destination);

  // 위치 보정 미리보기 (현재 선택된 모드 기반)
  const locationAdjustments = useMemo(() => {
    const allItems = [
      ...items.map((i) => ({
        uid: i.uid,
        basePrice: i.basePrice,
        locationInsideExtra: i.locationInsideExtra ?? 0,
        locationParkingDiscount: i.locationParkingDiscount ?? 0,
      })),
      ...(itemInfo.itemId
        ? [
            {
              uid: "_current",
              basePrice: itemInfo.basePrice,
              locationInsideExtra: itemInfo.locationInsideExtra ?? 0,
              locationParkingDiscount: itemInfo.locationParkingDiscount ?? 0,
            },
          ]
        : []),
    ];
    return calcLocationAdjustments(
      allItems,
      origin?.locationType,
      destination?.locationType,
    );
  }, [items, itemInfo, origin?.locationType, destination?.locationType]);

  // ── 검증 ──
  const checkSideComplete = (
    info: typeof origin,
    areaError: AreaError | null,
    sideAdjustment: { surcharge: number; discount: number },
  ): boolean => {
    if (!info?.address || !info?.lat || !info?.lng) return false;
    if (areaError) return false;
    if (!info.locationType) return false;
    if (info.locationType === "PARKING") {
      return !!info.parkingInfo && info.parkingInfo.trim().length > 0;
    }
    // INSIDE / DOOR
    if (!info.addressDetail) return false;
    if (info.floor === undefined || info.floor === -99) return false;
    // 운반도움 필수 + 비-PARKING side 일 때만 강제
    if (carryHelpRequired && !info.carryHelp) return false;
    void sideAdjustment;
    return true;
  };

  const originComplete = checkSideComplete(origin, originAreaError, {
    surcharge: locationAdjustments.originSurcharge,
    discount: locationAdjustments.originDiscount,
  });
  const destComplete = checkSideComplete(destination, destAreaError, {
    surcharge: locationAdjustments.destSurcharge,
    discount: locationAdjustments.destDiscount,
  });
  const isComplete = originComplete && destComplete;

  // ── side panel 렌더링 ──
  const renderSidePanel = (
    side: "origin" | "destination",
    info: typeof origin,
    setInfo: typeof setOrigin,
    sideAdjustment: { surcharge: number; discount: number },
  ) => {
    if (!info?.locationType) return null;
    const guidance = SIDE_GUIDANCE[side][info.locationType];

    const surchargeLabel = side === "origin" ? "집안수거 할증" : "집안배송 할증";
    const discountLabel = side === "origin" ? "주차장수거 할인" : "주차장배송 할인";

    return (
      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
          <p className="text-sm text-blue-800 whitespace-pre-line">{guidance}</p>
          {info.locationType === "INSIDE" && (
            <p className="text-sm font-semibold text-amber-700">
              {quoteMode || sideAdjustment.surcharge <= 0
                ? surchargeLabel
                : `+${sideAdjustment.surcharge.toLocaleString()}원`}
            </p>
          )}
          {info.locationType === "PARKING" && (
            <p className="text-sm font-semibold text-emerald-700">
              {quoteMode || sideAdjustment.discount <= 0
                ? discountLabel
                : `-${sideAdjustment.discount.toLocaleString()}원`}
            </p>
          )}
        </div>

        {info.locationType === "PARKING" ? (
          <div className="space-y-1">
            <label className="text-sm text-gray-600">
              주차 위치 &amp; 진입 방법<span className="text-red-500 ml-0.5">*</span>
            </label>
            <Textarea
              placeholder="예: 지하2층 주차장 102동 1,2호라인 앞, 정문에서 세대 호출"
              value={info.parkingInfo || ""}
              onChange={(e) => setInfo({ ...info, parkingInfo: e.target.value })}
              rows={3}
              className={cn(
                attempted && !info.parkingInfo && "border-red-500 ring-2 ring-red-200",
              )}
            />
            {attempted && !info.parkingInfo && (
              <p className="text-sm text-red-500">주차 위치 &amp; 진입 방법을 입력해주세요</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <AddressDetailInput
              value={info.addressDetail || ""}
              onChange={(v) => setInfo({ ...info, addressDetail: v })}
              invalid={attempted && !info.addressDetail}
            />
            <Step2ElevatorSelect
              hasElevator={info.hasElevator}
              floor={info.floor}
              carryHelp={info.carryHelp}
              floorPlaceholder="물품 운반 경로에 있는 계단 층수를 선택해주세요"
              onElevatorChange={(has) =>
                setInfo({ ...info, hasElevator: has, ...(has ? { floor: 0 } : {}) })
              }
              onFloorChange={(f) => setInfo({ ...info, floor: f })}
              onCarryHelpChange={(help) => setInfo({ ...info, carryHelp: help })}
              attempted={attempted}
              carryHelpRequired={carryHelpRequired}
            />
          </div>
        )}

      </div>
    );
  };

  return (
    <div className="space-y-6">
      {areaText && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          {areaText}
        </div>
      )}
      <div>
        <p className="text-sm text-gray-500">주소를 입력한 뒤 수거/배송 위치를 선택해주세요</p>
      </div>

      {carryHelpRequired && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          선택하신 물품은 운반에 도움이 필요합니다. 집안/문앞 수거·배송 시 <strong>&apos;운반 도움 가능&apos;</strong> 을 체크해야 합니다. (주차장 수거/배송은 제외)
        </div>
      )}

      {/* 출발지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="font-semibold text-[#E8723A]">출발지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#E8723A"
          value={originInfo}
          onChange={handleOriginChange}
          attempted={attempted}
          hideDetail
        />

        {originAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-red-700">
              죄송합니다. {originAreaError.city} {originAreaError.district}는 아직 정식 서비스 지역이 아닙니다. 견적 문의로 요청해주시면 일정과 물품에 따라 견적을 안내 드리도록 하겠습니다.
            </p>
            {onSwitchToQuote && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100"
                onClick={onSwitchToQuote}
              >
                견적 문의
              </Button>
            )}
          </div>
        )}

        {origin?.address && !originAreaError && (
          <>
            <Step2LocationTypePicker
              side="origin"
              value={origin.locationType}
              onChange={(t) => setOrigin({ ...origin, locationType: t })}
              disabledNonInsideReason={insidePickupRequired ? INSIDE_PICKUP_MESSAGE : null}
              attempted={attempted}
            />
            {renderSidePanel("origin", origin, setOrigin, {
              surcharge: locationAdjustments.originSurcharge,
              discount: locationAdjustments.originDiscount,
            })}
          </>
        )}
      </div>

      {/* 구분선 */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-gray-400 text-lg">↓</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* 도착지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-[#1A3A5C]" />
          <span className="font-semibold text-[#1A3A5C]">도착지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#1A3A5C"
          value={destInfo}
          onChange={handleDestinationChange}
          attempted={attempted}
          hideDetail
        />

        {destAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-red-700">
              죄송합니다. {destAreaError.city} {destAreaError.district}는 아직 정식 서비스 지역이 아닙니다. 견적 문의로 요청해주시면 일정과 물품에 따라 견적을 안내 드리도록 하겠습니다.
            </p>
            {onSwitchToQuote && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100"
                onClick={onSwitchToQuote}
              >
                견적 문의
              </Button>
            )}
          </div>
        )}

        {destination?.address && !destAreaError && (
          <>
            <Step2LocationTypePicker
              side="destination"
              value={destination.locationType}
              onChange={(t) => setDestination({ ...destination, locationType: t })}
              disabledNonInsideReason={insideDeliveryRequired ? INSIDE_DELIVERY_MESSAGE : null}
              attempted={attempted}
            />
            {renderSidePanel("destination", destination, setDestination, {
              surcharge: locationAdjustments.destSurcharge,
              discount: locationAdjustments.destDiscount,
            })}
          </>
        )}
      </div>

      {isComplete && (
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <p className="text-sm text-green-700 font-medium">✅ 출발지 · 도착지 입력 완료</p>
        </div>
      )}

      <Button
        onClick={() => {
          if (!isAdmin && !isComplete) {
            setAttempted(true);
            return;
          }
          onComplete();
        }}
        className={cn("w-full", !isAdmin && !isComplete && "bg-gray-300 hover:bg-gray-400 text-gray-700")}
        size="lg"
      >
        다음 단계로
      </Button>
    </div>
  );
}
